import {useEffect, useState} from 'react'
import {Description, Field, Label} from '@headlessui/react'
import {useTranslation} from 'react-i18next'

import {Button, DialogNew, Headline, Toggle} from '@sdox/ui'
import {useCurrentOffice} from '@sdox/api'

import {useCookies} from '../../../../hooks/useCookies'

import type {CookieOption} from '@sdox/utils/dom'

export interface CookieSettingsProps {
  onClose: (closeOptions: {didSave: boolean}) => void
}

export const CookieSettingsContent = ({onClose}: CookieSettingsProps) => {
  const office = useCurrentOffice()
  const options = office?.cookieContext.cookieOptions ?? []

  const {selectedCookieOptions: selected, acceptCookies} = useCookies({cookieOptions: options})
  const [currentSelection, setCurrentSelection] = useState(selected)
  const {t} = useTranslation()

  useEffect(() => {
    setCurrentSelection(selected)
  }, [selected, setCurrentSelection])

  const isSelected = (option: CookieOption): boolean => {
    return !!currentSelection.find((i) => i.key === option.key)
  }

  const handleChange = (option: CookieOption) => {
    if (option.vital) return

    const checked = isSelected(option)

    if (checked) {
      setCurrentSelection(currentSelection.filter((i) => i.key !== option.key))
    } else {
      setCurrentSelection([...currentSelection, option])
    }
  }

  const handleClose = () => {
    onClose({didSave: false})
  }

  const handleAcceptAll = async () => {
    setCurrentSelection(options)
    acceptCookies(options)
    onClose({didSave: true})
  }

  const handleAcceptSelected = async () => {
    acceptCookies(currentSelection)
    onClose({didSave: true})
  }

  return (
    <>
      <DialogNew.Content className={'pt-spacing-16'}>
        <div className={'flex flex-col'}>
          <ul>
            {options.map((option) => (
              <Field
                as={'li'}
                key={option.key}
                className={'mb-6 flex gap-8 border-b pb-6 last:mb-0 last:border-0 last:pb-0'}
              >
                <div>
                  <Label
                    as={Headline}
                    tag={'h4'}
                    className={'mb-4'}
                    size={4}
                  >
                    {option.headline}
                  </Label>
                  <Description as={'p'}>{option.text}</Description>
                </div>
                {!option.vital && (
                  <Toggle
                    checked={isSelected(option)}
                    onChange={() => handleChange(option)}
                  />
                )}
              </Field>
            ))}
          </ul>
        </div>
      </DialogNew.Content>
      <DialogNew.Footer className={'flex flex-col-reverse justify-between gap-spacing-12 sm:flex-row'}>
        <Button
          variant={'outline'}
          size={'medium'}
          onClick={handleClose}
        >
          {t('common:close')}
        </Button>
        <div className={'flex flex-col gap-spacing-12 sm:flex-row'}>
          <Button
            variant={'filled-brand'}
            size={'medium'}
            onClick={handleAcceptSelected}
          >
            {t('common:saveSettings')}
          </Button>
          <Button
            variant={'filled-brand'}
            size={'medium'}
            onClick={handleAcceptAll}
          >
            {t('common:acceptAll')}
          </Button>
        </div>
      </DialogNew.Footer>
    </>
  )
}
